"use client";

import { useActionState, useEffect, useState, startTransition } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { emailSchema, passwordSchema } from "@src/validation/schemas";
import { Input } from "@src/components/ui/input";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@src/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@src/components/ui/select";
import { MultiSelect } from "@src/components/ui/multi-select";
import { paths } from "@src/utils";
import { EXAMPLE_EMAIL, INTENTS, ORIGINS, PURPOSES } from "@src/constants";
import { posthog, AnalyticsEvent } from "@src/analytics";
import { toast } from "sonner";
import { useRouter } from "next/navigation";
import { InputOTP, InputOTPGroup, InputOTPSeparator, InputOTPSlot } from "@src/components/ui/input-otp";
import { config } from "@lib/utils/config";
import { useSupabase } from "../../../_components/providers/supabase-provider";
import { registerUser, type ActionState, type RegisterFormInput } from "../../actions";
import { AuthCard, AuthButton, FormAlert } from "../../_components";
import { PasswordInput } from "../../../_components/PasswordInput";
import { ToggleGroup, ToggleGroupItem } from "@src/components/ui/toggle-group";
import Link from "next/link";

// Form validation schema
const formSchema = z.object({
  name: z.string().min(2, {
    message: "Full name must be at least 2 characters."
  }),
  email: emailSchema,
  password: passwordSchema,
  organization: z.string().optional(),
  purposes: z.array(z.string()).min(1, {
    message: "Please select at least one purpose."
  }),
  intent: z.string({
    error: () => "Please select how you intend to use TrackShare."
  }).min(1, {
    message: "Please select how you intend to use TrackShare."
  }),
  origin: z.string({
    error: issue => issue.input === undefined ? "Please select how you heard about us." : "Not a string"
  })
});
export default function RegisterForm() {
  const [state, formAction, isPending] = useActionState<ActionState, RegisterFormInput>(registerUser, {
    result: "idle"
  });
  const {
    supabase
  } = useSupabase();
  const [email, setEmail] = useState<string | null>(null);
  const [otp, setOtp] = useState<string | null>(null);
  const router = useRouter();

  // Initialize form
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      password: "",
      organization: "",
      purposes: [],
      intent: "",
      origin: undefined
    }
  });

  // Handle action state changes
  useEffect(() => {
    if (state.result === "success") {
      const values = form.getValues();
      posthog.capture(AnalyticsEvent.USER_REGISTERED, {
        organization: values.organization || null,
        purposes: values.purposes,
        intent: values.intent,
        origin: values.origin
      });
      posthog.people.set({
        purposes: values.purposes,
        invited: false,
        intent: values.intent,
        origin: values.origin
      });
    }
  }, [state, form]);

  // Form submission handler
  function onSubmit(values: z.infer<typeof formSchema>) {
    setEmail(values.email);
    startTransition(() => {
      formAction({
        email: values.email,
        password: values.password,
        name: values.name,
        organization: values.organization || null,
        purposes: values.purposes,
        intent: values.intent,
        origin: values.origin,
        redirectTo: `${config.baseUrl}${paths.authCallback}`
      });
    });
  }
  const verifyOtp = async () => {
    if (!email) {
      throw new Error("Email is not set");
    }
    if (!otp) {
      throw new Error("OTP is not set");
    }
    if (otp.length !== 6) {
      throw new Error("OTP is not 6 digits");
    }
    const {
      error
    } = await supabase.auth.verifyOtp({
      email,
      token: otp,
      type: "signup"
    });
    if (error) {
      toast.error(error.message, {
        duration: 5000
      });
    } else {
      router.push(paths.home);
    }
  };
  const renderForm = () => {
    if (state.result === "success") {
      return <div className="space-y-6">
          <FormAlert state={{
          result: "success",
          message: "Registration successful! Please check your email for a confirmation link, or enter the 6-digit code below to verify your email address."
        }} isPending={false} />

          <InputOTP maxLength={6} onChange={setOtp} onComplete={verifyOtp} containerClassName="justify-center">
            <InputOTPGroup>
              <InputOTPSlot index={0} />
              <InputOTPSlot index={1} />
              <InputOTPSlot index={2} />
            </InputOTPGroup>
            <InputOTPSeparator />
            <InputOTPGroup>
              <InputOTPSlot index={3} />
              <InputOTPSlot index={4} />
              <InputOTPSlot index={5} />
            </InputOTPGroup>
          </InputOTP>
        </div>;
    }
    return <Form {...form} data-sentry-element="Form" data-sentry-component="renderForm" data-sentry-source-file="register-form.tsx">
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6" noValidate>
          <FormField control={form.control} name="name" render={({
          field
        }) => <FormItem>
                <FormLabel>Full name</FormLabel>
                <FormControl>
                  <Input placeholder="John Doe" {...field} ref={undefined} />
                </FormControl>
                <FormMessage />
              </FormItem>} data-sentry-element="FormField" data-sentry-source-file="register-form.tsx" />

          <FormField control={form.control} name="email" render={({
          field
        }) => <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input type="email" placeholder={EXAMPLE_EMAIL} {...field} ref={undefined} />
                </FormControl>
                <FormMessage />
              </FormItem>} data-sentry-element="FormField" data-sentry-source-file="register-form.tsx" />

          <FormField control={form.control} name="password" render={({
          field
        }) => <FormItem>
                <FormLabel>Password</FormLabel>
                <FormControl>
                  <PasswordInput {...field} ref={undefined} />
                </FormControl>
                {!!form.formState.errors.password || <FormDescription>Must be at least 10 characters</FormDescription>}
                <FormMessage />
              </FormItem>} data-sentry-element="FormField" data-sentry-source-file="register-form.tsx" />

          <FormField control={form.control} name="organization" render={({
          field
        }) => <FormItem>
                <FormLabel>Organization</FormLabel>
                <FormControl>
                  <Input placeholder="Your organization (optional)" {...field} ref={undefined} />
                </FormControl>
                <FormMessage />
              </FormItem>} data-sentry-element="FormField" data-sentry-source-file="register-form.tsx" />

          <FormField control={form.control} name="purposes" render={({
          field
        }) => <FormItem className="mb-2">
                <FormLabel>Purpose</FormLabel>
                <FormDescription>What will you be using TrackShare for?</FormDescription>
                <FormControl>
                  <MultiSelect options={PURPOSES} selected={field.value} onChange={field.onChange} placeholder="Select purposes" />
                </FormControl>
                <FormMessage />
              </FormItem>} data-sentry-element="FormField" data-sentry-source-file="register-form.tsx" />

          <FormField control={form.control} name="intent" render={({
          field
        }) => <FormItem>
                {/*<FormLabel>What brings you to TrackShare?</FormLabel>*/}
                <FormDescription>Do you intend to create songs, or just listen?</FormDescription>
                <FormControl>
                  <ToggleGroup type="single" value={field.value} onValueChange={value => {
              if (value) field.onChange(value);
            }} className="w-full" variant={"outline"}>
                    {INTENTS.map(intent => <ToggleGroupItem key={intent.value} value={intent.value} className="flex-1">
                        {intent.label}
                      </ToggleGroupItem>)}
                  </ToggleGroup>
                </FormControl>
                <FormMessage />
              </FormItem>} data-sentry-element="FormField" data-sentry-source-file="register-form.tsx" />

          <FormField control={form.control} name="origin" render={({
          field
        }) => <FormItem>
                <FormLabel>How did you hear about TrackShare?</FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select an option" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {ORIGINS.map(option => <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>)}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>} data-sentry-element="FormField" data-sentry-source-file="register-form.tsx" />

          <AuthButton isPending={isPending} text="Register" pendingText="Registering..." data-ph-capture-attribute-action="auth-register-submit" data-sentry-element="AuthButton" data-sentry-source-file="register-form.tsx" />

          <FormAlert state={state} isPending={isPending} data-sentry-element="FormAlert" data-sentry-source-file="register-form.tsx" />
        </form>
      </Form>;
  };
  return <AuthCard title="Create an account" className="max-w-md" footer={<p className="text-sm text-muted-foreground">
          Already have an account?{" "}
          <Link href={paths.signIn()} className="text-primary font-medium hover:underline">
            Sign in
          </Link>
        </p>} data-sentry-element="AuthCard" data-sentry-component="RegisterForm" data-sentry-source-file="register-form.tsx">
      {renderForm()}
    </AuthCard>;
}